import React, { useEffect, useState } from 'react'
import { ProgressBar } from '@fluentui/react-components'
import type { ProgressBarProps } from '@fluentui/react-components'
import { useInViewOnce } from '../../hooks/useInViewOnce'
import { useEffects } from '../../contexts/EffectsContext'

interface AnimatedProgressBarProps extends Omit<ProgressBarProps, 'value'> {
	/** Target value 0–1 */
	value: number
	/** Animation duration in ms */
	duration?: number
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

/**
 * ProgressBar that animates from 0 to its target value the first time it
 * scrolls into view. With 'Scroll animations' disabled it renders the
 * target value directly.
 */
export const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
	value,
	duration = 900,
	...rest
}) => {
	const { effects } = useEffects()
	const animate = effects.scrollReveal
	const [ref, inView] = useInViewOnce<HTMLDivElement>()
	const [current, setCurrent] = useState(0)

	useEffect(() => {
		if (!animate || !inView) return
		let frame: number
		const start = performance.now()
		const step = (now: number) => {
			const progress = Math.min((now - start) / duration, 1)
			setCurrent(value * easeOutCubic(progress))
			if (progress < 1) {
				frame = requestAnimationFrame(step)
			}
		}
		frame = requestAnimationFrame(step)
		return () => cancelAnimationFrame(frame)
	}, [animate, inView, value, duration])

	// With the effect off the target value renders directly — the animated
	// state is only consulted while animating.
	return (
		<div ref={ref}>
			<ProgressBar value={animate ? current : value} {...rest} />
		</div>
	)
}

export default AnimatedProgressBar
